import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { CheckCircle2, Loader2, TriangleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  codeResetSchema,
  forgotSchema,
  requestPasswordReset,
  resetSchema,
  submitPasswordReset,
  submitPasswordResetCode,
  type CodeResetFormValues,
  type ForgotFormValues,
  type ResetFormValues,
} from '@/lib/relay';

/**
 * One page, three states. A `?token=` in the URL asks for a new password
 * straight away — that's what an emailed link carries. Without one, a parent
 * starts on the email step and can switch to the code step ("Got a code
 * instead?") if they have the code but not the link — the same device the
 * email is open on, say. The token flow is untouched by any of this.
 */
export default function ResetPage() {
  // Read once, before the token gets stripped from the address bar below —
  // re-reading window.location.search on every render would find nothing
  // the instant that happens. `window` is absent during the build-time
  // prerender (BRA-100), so fall back to no token there.
  const [token] = useState(() =>
    typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('token'),
  );
  const [view, setView] = useState<'email' | 'code'>('email');

  useEffect(() => {
    // A reset token sitting in the URL survives in browser history and can
    // leak through a Referer header on the next outbound request from this
    // tab. Scoped to this page rather than added site-wide in index.html,
    // since nowhere else on the site ever carries a secret in its URL.
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    if (!token) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }, [token]);

  return (
    <div className="container max-w-md py-16 sm:py-24">
      <header>
        <p className="kicker">Reset password</p>
        <h1 className="mt-4 text-[clamp(2.2rem,6vw,3.2rem)] leading-[1.02]">
          {token ? 'Choose a new password' : view === 'code' ? 'Enter your code' : 'Reset your password'}
        </h1>
      </header>

      <div className="hairline my-10" />

      {token ? (
        <ResetForm token={token} />
      ) : view === 'code' ? (
        <CodeResetForm onUseLink={() => setView('email')} />
      ) : (
        <ForgotForm onUseCode={() => setView('code')} />
      )}
    </div>
  );
}

function ForgotForm({ onUseCode }: { onUseCode: () => void }) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotFormValues>({
    resolver: zodResolver(forgotSchema),
    mode: 'onBlur',
    defaultValues: { email: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: ForgotFormValues) => requestPasswordReset(values.email),
  });

  if (mutation.isSuccess) {
    return (
      <p className="flex items-start gap-2 rounded-md border border-leaf/40 bg-leaf/10 px-4 py-3 text-sm text-foreground">
        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-leaf" aria-hidden="true" />
        If that address has an account, a reset link is on its way.
      </p>
    );
  }

  const busy = isSubmitting || mutation.isPending;

  return (
    <form noValidate onSubmit={handleSubmit((values) => mutation.mutateAsync(values))} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@example.com"
          aria-invalid={errors.email ? 'true' : 'false'}
          aria-describedby={errors.email ? 'email-error' : undefined}
          {...register('email')}
        />
        {errors.email ? (
          <p id="email-error" role="alert" className="text-sm font-medium text-destructive">
            {errors.email.message}
          </p>
        ) : null}
      </div>

      <Button type="submit" size="lg" disabled={busy}>
        {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
        {busy ? 'Sending…' : 'Send reset link'}
      </Button>

      <button
        type="button"
        onClick={onUseCode}
        className="block text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
      >
        Got a code instead?
      </button>
    </form>
  );
}

function CodeResetForm({ onUseLink }: { onUseLink: () => void }) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CodeResetFormValues>({
    resolver: zodResolver(codeResetSchema),
    mode: 'onBlur',
    defaultValues: { email: '', code: '', password: '', confirm: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: CodeResetFormValues) =>
      submitPasswordResetCode(values.email, values.code, values.password),
  });

  if (mutation.isSuccess) {
    return (
      <p className="flex items-start gap-2 rounded-md border border-leaf/40 bg-leaf/10 px-4 py-3 text-sm text-foreground">
        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-leaf" aria-hidden="true" />
        Password changed. Sign in on the app.
      </p>
    );
  }

  const busy = isSubmitting || mutation.isPending;

  return (
    <form
      noValidate
      onSubmit={handleSubmit((values) => mutation.mutateAsync(values).catch(() => undefined))}
      className="space-y-6"
    >
      <div className="space-y-2">
        <Label htmlFor="code-email">Email</Label>
        <Input
          id="code-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@example.com"
          aria-invalid={errors.email ? 'true' : 'false'}
          aria-describedby={errors.email ? 'code-email-error' : undefined}
          {...register('email')}
        />
        {errors.email ? (
          <p id="code-email-error" role="alert" className="text-sm font-medium text-destructive">
            {errors.email.message}
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="code">Code</Label>
        <Input
          id="code"
          type="text"
          autoComplete="one-time-code"
          placeholder="ABCD-2345"
          aria-invalid={errors.code ? 'true' : 'false'}
          aria-describedby={errors.code ? 'code-error' : undefined}
          {...register('code')}
        />
        {errors.code ? (
          <p id="code-error" role="alert" className="text-sm font-medium text-destructive">
            {errors.code.message}
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="code-password">New password</Label>
        <Input
          id="code-password"
          type="password"
          autoComplete="new-password"
          aria-invalid={errors.password ? 'true' : 'false'}
          aria-describedby={errors.password ? 'code-password-error' : undefined}
          {...register('password')}
        />
        {errors.password ? (
          <p id="code-password-error" role="alert" className="text-sm font-medium text-destructive">
            {errors.password.message}
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="code-confirm">Confirm password</Label>
        <Input
          id="code-confirm"
          type="password"
          autoComplete="new-password"
          aria-invalid={errors.confirm ? 'true' : 'false'}
          aria-describedby={errors.confirm ? 'code-confirm-error' : undefined}
          {...register('confirm')}
        />
        {errors.confirm ? (
          <p id="code-confirm-error" role="alert" className="text-sm font-medium text-destructive">
            {errors.confirm.message}
          </p>
        ) : null}
      </div>

      <Button type="submit" size="lg" disabled={busy}>
        {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
        {busy ? 'Saving…' : 'Set new password'}
      </Button>

      <button
        type="button"
        onClick={onUseLink}
        className="block text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
      >
        Use a link instead?
      </button>

      <div aria-live="polite">
        {mutation.isError ? (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-foreground"
          >
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden="true" />
            {mutation.error instanceof Error ? mutation.error.message : 'Something went wrong. Please try again.'}
          </p>
        ) : null}
      </div>
    </form>
  );
}

function ResetForm({ token }: { token: string }) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetFormValues>({
    resolver: zodResolver(resetSchema),
    mode: 'onBlur',
    defaultValues: { password: '', confirm: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: ResetFormValues) => submitPasswordReset(token, values.password),
  });

  if (mutation.isSuccess) {
    return (
      <p className="flex items-start gap-2 rounded-md border border-leaf/40 bg-leaf/10 px-4 py-3 text-sm text-foreground">
        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-leaf" aria-hidden="true" />
        Password changed. Sign in on the app.
      </p>
    );
  }

  const busy = isSubmitting || mutation.isPending;

  return (
    <form
      noValidate
      onSubmit={handleSubmit((values) => mutation.mutateAsync(values).catch(() => undefined))}
      className="space-y-6"
    >
      <div className="space-y-2">
        <Label htmlFor="password">New password</Label>
        <Input
          id="password"
          type="password"
          autoComplete="new-password"
          aria-invalid={errors.password ? 'true' : 'false'}
          aria-describedby={errors.password ? 'password-error' : undefined}
          {...register('password')}
        />
        {errors.password ? (
          <p id="password-error" role="alert" className="text-sm font-medium text-destructive">
            {errors.password.message}
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirm">Confirm password</Label>
        <Input
          id="confirm"
          type="password"
          autoComplete="new-password"
          aria-invalid={errors.confirm ? 'true' : 'false'}
          aria-describedby={errors.confirm ? 'confirm-error' : undefined}
          {...register('confirm')}
        />
        {errors.confirm ? (
          <p id="confirm-error" role="alert" className="text-sm font-medium text-destructive">
            {errors.confirm.message}
          </p>
        ) : null}
      </div>

      <Button type="submit" size="lg" disabled={busy}>
        {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
        {busy ? 'Saving…' : 'Set new password'}
      </Button>

      <div aria-live="polite">
        {mutation.isError ? (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-foreground"
          >
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden="true" />
            {mutation.error instanceof Error ? mutation.error.message : 'Something went wrong. Please try again.'}
          </p>
        ) : null}
      </div>
    </form>
  );
}
